import React from "react";
import { getSessionDetails } from "../utils/userSession";
import moment from "moment";
import { UserSessionDialogProps, getContextRows } from "./user_session/UserSessionDialog";
import TitledDialog from "./TitledDialog";
import { Button, Typography, Paper } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { styled } from "@mui/material/styles";
import { FormattedMessage } from "react-intl";

// Styled components for the grid
const LabelGrid = styled(Grid)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1.5),
    fontWeight: "bold",
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ValueGrid = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

// Default props for the grid components
LabelGrid.defaultProps = {
    size: { xs: 12, sm: 4 },
};

ValueGrid.defaultProps = {
    size: { xs: 12, sm: 8 },
};

const SessionDetailsContainer = styled(Paper)(({ theme }) => ({
    marginTop: theme.spacing(2),
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

function label(name: string) {
    return <FormattedMessage id={`user_session.${name}`} />;
}

function unknown() {
    return label("unknown");
}

const UserServiceSessionDialog = ({
    onHide,
    onExited,
    onDelete,
    session,
    show,
    disableDeleteCurrentSession,
}: UserSessionDialogProps) => {
    const getAppName = () => {
        return session["apps"][0]["name"];
    };

    const getBody = () => {
        const { lastUse, location } = getSessionDetails(session);

        const userAgent = session["user_agent"];

        const osVersion = userAgent && userAgent["os"]["version"].length > 0 ? ` (${userAgent["os"]["version"]})` : "";

        return (
            <SessionDetailsContainer elevation={0}>
                <Grid container>
                    <LabelGrid>{label("os")}</LabelGrid>
                    <ValueGrid>{userAgent ? `${userAgent["os"]["name"]}${osVersion}` : unknown()}</ValueGrid>

                    <LabelGrid>{label("client")}</LabelGrid>
                    <ValueGrid>
                        {userAgent ? `${userAgent["client"]["name"]} (${userAgent["client"]["version"]})` : unknown()}
                    </ValueGrid>

                    <LabelGrid>{label("ip")}</LabelGrid>
                    <ValueGrid>{session["ip"] || unknown()}</ValueGrid>

                    <LabelGrid>{label("location")}</LabelGrid>
                    <ValueGrid>{location}</ValueGrid>

                    <LabelGrid>{label("createdService")}</LabelGrid>
                    <ValueGrid>
                        {moment(session["created"]).format("LLL")}
                        {` (${moment
                            .duration(moment.duration(moment(session["created"]).diff(moment())))
                            .humanize(true)})`}
                    </ValueGrid>

                    <LabelGrid>{label("last_use")}</LabelGrid>
                    <ValueGrid>
                        {session["last_use"] ? (
                            <span>
                                {moment(session["last_use"]).format("LLL")} ({lastUse})
                            </span>
                        ) : (
                            label("never")
                        )}
                    </ValueGrid>

                    <LabelGrid>{label("app_identifier")}</LabelGrid>
                    <ValueGrid>{session["apps"][0]["identifier"]}</ValueGrid>

                    {getContextRows(session)}
                </Grid>
            </SessionDetailsContainer>
        );
    };

    return (
        <TitledDialog
            open={show}
            isContentChanged={() => false}
            hide={onHide}
            onExited={onExited}
            title={
                <>
                    {label("dialog_title_service")}
                    <Typography component="span" style={{ paddingLeft: 5 }}>
                        {getAppName()}
                    </Typography>
                </>
            }
            actions={
                session["current"] && disableDeleteCurrentSession ? null : (
                    <Button onClick={onDelete} color="error">
                        <FormattedMessage id="user_session.delete" />
                    </Button>
                )
            }
        >
            {getBody()}
        </TitledDialog>
    );
};

export default UserServiceSessionDialog;
